import { cn } from '@/lib/utils';

/**
 * Единые токены радиусов и карточек PWA (светлая/тёмная тема через CSS-переменные).
 * Контракт: предпочитать эти классы вместо произвольных rounded-[22px] и смеси slate/bg-[var(--surface)].
 */

export const pwaRadius = {
  /** Стандартная карточка, список, инпуты */
  card: 'rounded-2xl',
  /** Крупные панели, hero на дашборде */
  cardLg: 'rounded-3xl',
  /** Максимальный радиус (аналитика hero) */
  hero: 'rounded-[1.75rem]',
  /** Pills / CTA / segmented controls */
  pill: 'rounded-full',
  /** Bottom sheets and prominent floating panels */
  sheet: 'rounded-[1.75rem]',
} as const;

export const pwaSurface = {
  screen: 'bg-[var(--background)] dark:bg-[var(--background)]',
  elevated: 'bg-[var(--surface)] shadow-sm',
  card: 'bg-[var(--surface)] shadow-sm',
  muted: 'bg-[var(--surface-soft)] shadow-sm',
  list: 'bg-[var(--surface-strong)] border border-[var(--border)]/80 shadow-inner',
  panel: 'bg-[var(--surface)] shadow-sm',
  skeleton: 'bg-[var(--skeleton)]',
  skeletonStrong: 'bg-[var(--skeleton-strong)]',
} as const;

/** Обёртка содержимого карточки с единым паддингом */
export const pwaCardPadding = 'p-3.5 sm:p-4';

export function pwaCardClass(extra?: string) {
  return cn(pwaRadius.card, pwaSurface.card, pwaCardPadding, extra);
}

export function pwaMutedPanelClass(extra?: string) {
  return cn(pwaRadius.card, pwaSurface.muted, 'px-3 py-3 text-sm text-[var(--muted-foreground)]', extra);
}

export function pwaPanelClass(extra?: string) {
  return cn(pwaRadius.cardLg, pwaSurface.panel, 'p-4 sm:p-5', extra);
}

export function pwaListShellClass(extra?: string) {
  return cn(pwaRadius.cardLg, pwaSurface.list, extra);
}

/** Вертикальная лента независимых карточек на фоне страницы (без общей рамки). */
export function pwaFeedClass(extra?: string) {
  return cn('flex flex-col gap-2', extra);
}

/** Одна плитка в ленте: мягкая filled-капсула (семья с полями). */
export function pwaFeedCardClass(extra?: string) {
  return cn(
    'rounded-2xl border-0 bg-[var(--surface)] px-3.5 py-3.5 shadow-sm sm:px-4',
    extra
  );
}

export function pwaFeedCardInteractiveClass(extra?: string) {
  return cn(
    'text-left transition hover:bg-[var(--surface-soft)] active:bg-[var(--surface-strong)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/45 focus-visible:ring-offset-2',
    'ring-offset-[var(--background)]',
    extra
  );
}

export function pwaFeedCardUnreadClass(extra?: string) {
  return cn('bg-[var(--primary-soft)]/40 dark:bg-primary-950/25', extra);
}

// ─── Form fields (Dodo-like filled; семья с pwaSearchFieldClass) ───

const pwaFieldFocusClass = cn(
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/45 focus-visible:ring-offset-0'
);

/**
 * Каноническое filled-поле (формы, sheet). Search stadium — отдельно `pwaSearchFieldClass`.
 */
export const pwaFieldClass = cn(
  'w-full rounded-xl border-0 bg-[var(--surface-strong)] px-3.5 py-3 text-sm text-[var(--foreground)]',
  'placeholder:text-[var(--muted-foreground)]',
  'disabled:cursor-not-allowed disabled:opacity-50',
  pwaFieldFocusClass
);

export const pwaFieldTextareaClass = cn(pwaFieldClass, 'min-h-[5.5rem] overflow-y-auto resize-none');

export const pwaFieldCompactClass = cn(
  'w-full min-w-0 rounded-lg border-0 bg-[var(--surface-strong)] px-2.5 py-2 text-sm text-[var(--foreground)]',
  'placeholder:text-[var(--muted-foreground)]',
  'disabled:cursor-not-allowed disabled:opacity-50',
  pwaFieldFocusClass
);

/** Native `<select>` в семье filled-полей (chevron через pr). */
export const pwaSelectClass = cn(pwaFieldClass, 'appearance-none pr-10');

/** type=date / datetime-local — filled + overflow-safe wrapper снаружи. */
export const pwaDateFieldClass = cn(
  pwaFieldClass,
  'appearance-none tabular-nums [color-scheme:light] dark:[color-scheme:dark]'
);

export const pwaDatetimeLocalFieldClass = cn(
  pwaFieldClass,
  'appearance-none text-base tabular-nums [color-scheme:light] dark:[color-scheme:dark]'
);

// ─── Consumable request forms (flat fields, shared with list detail modals) ───

const pwaRequestFormControlFocusClass = pwaFieldFocusClass;

/** @deprecated Prefer `pwaFieldClass` — оставлен как alias для существующих форм. */
export const pwaRequestFormFieldClass = cn(
  'w-full rounded-xl bg-[var(--surface-strong)] px-3 py-2.5 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)]',
  pwaRequestFormControlFocusClass
);

/** Компактное поле (строки редактирования, узкие ячейки) */
export const pwaRequestFormFieldCompactClass = pwaFieldCompactClass;

/** Мелкий контрол в таблице позиций (создание заявки) */
export const pwaRequestFormFieldTableClass = cn(
  'w-full min-w-0 rounded-lg bg-[var(--surface-strong)] px-2 py-2 text-xs text-[var(--foreground)]',
  pwaRequestFormControlFocusClass
);

export const pwaRequestFormTextareaClass = cn(pwaRequestFormFieldClass, 'resize-none');

/** Дата/время дедлайна */
export const pwaRequestFormDeadlineClass = cn(
  'box-border min-w-0 rounded-xl bg-[var(--surface-strong)] px-3 py-2 text-sm tabular-nums text-[var(--foreground)]',
  pwaRequestFormControlFocusClass
);

/** Секция формы без PwaPanel: отступ сверху + разделитель (кроме первой) */
export function pwaRequestFormSectionClass(extra?: string) {
  return cn(
    'min-w-0 max-w-full space-y-3 border-t border-[var(--border)]/70 pt-5 first:border-t-0 first:pt-0',
    extra
  );
}

export const pwaRequestFormSectionEyebrowClass =
  'text-[11px] font-semibold uppercase tracking-wide text-[var(--muted-foreground)]';

export const pwaRequestFormSectionHeadingClass = 'text-sm font-semibold text-[var(--foreground)]';

/** Короткое пояснение вместо тяжёлой карточки */
export const pwaRequestFormHintClass = cn(
  'border-l-2 border-primary-500/35 pl-3 text-sm text-[var(--muted-foreground)] dark:border-primary-400/30'
);

export function pwaSkeletonBlockClass(extra?: string) {
  return cn(pwaRadius.card, pwaSurface.skeleton, 'animate-pulse', extra);
}

export function pwaSkeletonHeroClass(extra?: string) {
  return cn(pwaRadius.hero, pwaSurface.skeletonStrong, 'animate-pulse', extra);
}

// ─── Tinted cards (PVZ_app: заливки вместо «белый + только обводка») ───

export type PwaTintedVariant =
  | 'mint'
  | 'mintSoft'
  | 'neutral'
  | 'amber'
  | 'rose'
  | 'slate';

const pwaTintedVariants: Record<PwaTintedVariant, string> = {
  mint:
    'border border-emerald-200/80 bg-[var(--surface-tint-primary)] shadow-sm dark:border-emerald-800/60 dark:bg-[var(--surface-tint-primary)]',
  mintSoft:
    'border border-emerald-200/70 bg-[var(--surface-tint-success)] shadow-sm dark:border-emerald-800/50 dark:bg-[var(--surface-tint-success)]',
  neutral:
    'bg-[var(--surface-tint-neutral)] shadow-sm dark:bg-[var(--surface-tint-neutral)]',
  amber:
    'border border-amber-200/80 bg-[var(--surface-tint-warning)] shadow-sm dark:border-amber-800/60 dark:bg-[var(--surface-tint-warning)]',
  rose:
    'border border-rose-200/80 bg-[var(--surface-tint-danger)] shadow-sm dark:border-rose-800/60 dark:bg-[var(--surface-tint-danger)]',
  slate:
    'bg-[var(--surface-tint-neutral)] shadow-sm dark:bg-[var(--surface-tint-neutral)]',
};

/** Карточка с цветной заливкой + стандартный паддинг. */
export function pwaTintedCardClass(variant: PwaTintedVariant, extra?: string) {
  return cn(pwaRadius.cardLg, pwaTintedVariants[variant], pwaCardPadding, extra);
}

/** То же без встроенного padding — для секций с собственной сеткой (divide, p-0). */
export function pwaTintedSurfaceClass(variant: PwaTintedVariant, extra?: string) {
  return cn(pwaRadius.cardLg, pwaTintedVariants[variant], extra);
}

/** Единый «лоток» для списка строк: тонированный фон, без рамки у каждой строки. */
export function pwaTintedListShellClass(extra?: string) {
  return cn(
    pwaRadius.cardLg,
    'overflow-hidden bg-[var(--surface-tint-neutral)] shadow-inner dark:bg-[var(--surface-tint-neutral)]',
    extra
  );
}

export const pwaStatusChipBaseClass = cn(
  pwaRadius.pill,
  'inline-flex items-center gap-1.5 border px-2.5 py-1 text-xs font-semibold'
);

/** Сплошные статусные бейджи (чек-листы и др. операционные метки). */
export const pwaChecklistStatusChipBaseClass = cn(
  pwaRadius.pill,
  'inline-flex items-center gap-1 border-0 px-2 py-0.5 text-[10px] font-semibold leading-none'
);

export const pwaChecklistStatusChipClass = {
  neutral: cn(
    pwaChecklistStatusChipBaseClass,
    'bg-slate-600 text-white dark:bg-slate-500 dark:text-white'
  ),
  accent: cn(
    pwaChecklistStatusChipBaseClass,
    'bg-primary-600 text-white dark:bg-primary-500 dark:text-white'
  ),
  success: cn(
    pwaChecklistStatusChipBaseClass,
    'bg-emerald-600 text-white dark:bg-emerald-500 dark:text-white'
  ),
  warning: cn(
    pwaChecklistStatusChipBaseClass,
    'bg-amber-500 text-white dark:bg-amber-500 dark:text-white'
  ),
  danger: cn(
    pwaChecklistStatusChipBaseClass,
    'bg-red-600 text-white dark:bg-red-500 dark:text-white'
  ),
  info: cn(
    pwaChecklistStatusChipBaseClass,
    'bg-sky-600 text-white dark:bg-sky-500 dark:text-white'
  ),
} as const;

export const pwaStatusChipClass = {
  neutral: cn(
    pwaStatusChipBaseClass,
    'border-[var(--status-neutral-border)] bg-[var(--status-neutral-bg)] text-[var(--status-neutral-fg)]'
  ),
  accent: cn(
    pwaStatusChipBaseClass,
    'border-[var(--status-accent-border)] bg-[var(--status-accent-bg)] text-[var(--status-accent-fg)]'
  ),
  success: cn(
    pwaStatusChipBaseClass,
    'border-[var(--status-success-border)] bg-[var(--status-success-bg)] text-[var(--status-success-fg)]'
  ),
  warning: cn(
    pwaStatusChipBaseClass,
    'border-[var(--status-warning-border)] bg-[var(--status-warning-bg)] text-[var(--status-warning-fg)]'
  ),
  danger: cn(
    pwaStatusChipBaseClass,
    'border-[var(--status-danger-border)] bg-[var(--status-danger-bg)] text-[var(--status-danger-fg)]'
  ),
} as const;

export const pwaPriorityChipClass = {
  low: pwaStatusChipClass.neutral,
  medium: pwaStatusChipClass.accent,
  high: pwaStatusChipClass.danger,
} as const;

// ─── Primary screen buttons (stadium-капсулы; низ экрана — см. PwaPrimaryPillButton с градиентом) ───

/** Заливка и hover/active для primary-кнопок и выбранных чипов на экране */
export const pwaPrimarySolidClass =
  'bg-primary-600 text-white transition-colors hover:bg-primary-700 active:bg-primary-800';

export const pwaPrimaryButtonDisabledClass =
  'disabled:pointer-events-none disabled:opacity-50 disabled:hover:bg-primary-600';

export const pwaPrimaryButtonFocusClass =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ring-offset-[var(--background)]';

/**
 * Вторичная экранная кнопка: inline stadium-капсула, text-sm.
 * Объединяйте с `className` для `w-full`, других отступов.
 */
export const pwaPrimaryButtonClass = cn(
  'inline-flex items-center justify-center gap-2 rounded-full font-medium text-sm',
  pwaPrimarySolidClass,
  pwaPrimaryButtonDisabledClass,
  pwaPrimaryButtonFocusClass,
  'px-4 py-2'
);

/** Компактная капсула (toolbar, мелкие действия в списках) */
export const pwaPrimaryButtonSmClass = cn(
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold text-xs',
  pwaPrimarySolidClass,
  pwaPrimaryButtonDisabledClass,
  pwaPrimaryButtonFocusClass,
  'px-3 py-1.5'
);

/** Блок на всю ширину карточки / формы — stadium */
export const pwaPrimaryButtonBlockClass = cn(
  'flex w-full items-center justify-center gap-2 rounded-full font-semibold text-sm',
  pwaPrimarySolidClass,
  pwaPrimaryButtonDisabledClass,
  pwaPrimaryButtonFocusClass,
  'px-4 py-3'
);

/** Только состояние «выбрано» в ternary (без flex/padding) */
export const pwaPrimarySelectedClass = pwaPrimarySolidClass;

/** Круг под иконку (меню вложений и т.п.), без hover как у кнопки */
export const pwaPrimaryDiscClass = 'bg-primary-600 text-white';

/** Пилла / бейдж с primary-заливкой (статус, выделение) */
export const pwaPrimaryFilledPillClass = 'bg-primary-600 text-white shadow-sm';

/** Статус «Открыта» и аналогичные success-pill на карточках смены. */
export const pwaShiftOpenStatusPillClass =
  'border-transparent bg-emerald-600 text-white shadow-sm dark:bg-emerald-600 dark:text-white';

/** Статус «Закрыта» — единая серая плашка независимо от compliance. */
export const pwaShiftClosedStatusPillClass =
  'border border-[var(--border)] bg-[var(--surface-strong)] text-[var(--foreground)]';

/** Вложенный графит внутри slate-карточки (темнее `--surface-tint-neutral`). */
export const pwaNestedGraphiteSurfaceClass =
  'min-w-0 overflow-hidden bg-[var(--surface)] shadow-sm';

/** Строка мета на карточке смены (сотрудник, точка, начало). */
export const pwaShiftMetaChipClass = cn(
  pwaNestedGraphiteSurfaceClass,
  'flex min-w-0 items-center gap-2 rounded-xl px-3 py-2'
);

/** Иконки в мета-строках карточки смены (время, точка, план). */
export const pwaShiftMetaIconClass =
  'h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400';

/** Плашка утверждённого времени корректировки. */
export const pwaShiftTimeApprovedPlaqueClass =
  'rounded-xl bg-emerald-100 px-3 py-2 text-xs text-emerald-950 dark:bg-emerald-500/25 dark:text-emerald-50';

/** Вторичное действие на всю ширину (серая stadium-капсула). */
export const pwaSecondaryButtonBlockClass = cn(
  'flex w-full items-center justify-center rounded-full px-4 py-3 text-sm font-semibold transition',
  'bg-[var(--surface-strong)] text-[var(--foreground)]',
  'hover:brightness-95 active:opacity-90',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]',
  'disabled:pointer-events-none disabled:opacity-50'
);

/** Опасное действие на всю ширину (принудительное закрытие смены). */
export const pwaDangerButtonBlockClass = cn(
  'flex w-full items-center justify-center rounded-full px-4 py-3 text-sm font-semibold transition',
  'bg-[var(--violation-cta-bg)] text-[var(--violation-cta-fg)]',
  'hover:bg-[var(--violation-cta-bg-hover)] active:opacity-90',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--violation-cta-bg)]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]',
  'disabled:pointer-events-none disabled:opacity-50'
);

/** Дорожка сегмент-навигации — soft track (Dodo-like), без «сырого» glass-шума. */
export const pwaGlassSegmentTrackClass = cn(
  'border border-[var(--border)]/70 bg-[var(--surface-strong)]/90 dark:bg-[var(--surface-strong)]/80'
);

/**
 * Непрозрачная дорожка шапок разделов (`slidingAccent`).
 * Light: `--surface-strong`. Dark: `--surface` (#1c1c1e), как карточки/CTA главной, не бледный `#3a3a3c`.
 * Не подставлять в sheet’ы — у них остаётся полупрозрачный `pwaGlassSegmentTrackClass`.
 */
export const pwaSectionSlidingTrackClass = cn(
  'border border-[var(--border)]/70 bg-[var(--surface-strong)] dark:bg-[var(--surface)]'
);

/** Цифра числа смен в ячейке календаря команды (низ-право). */
export const pwaShiftCalendarCountPillClass = cn(
  'absolute bottom-0.5 right-0.5 inline-flex min-h-[1.05rem] min-w-[1.05rem] items-center justify-center',
  'rounded-full bg-[var(--surface-strong)] px-1 py-px text-[9px] font-semibold tabular-nums leading-none',
  'text-[var(--muted-foreground)]'
);

/** Активный пункт основной навигации (tab bar, sidebar). */
export const pwaNavActiveClass = 'text-[var(--nav-active)]';

/** Неактивный пункт основной навигации. */
export const pwaNavInactiveClass = 'text-[var(--muted-foreground)]';

/** Область под активной иконкой в tab bar — только отступ, без серой подложки. */
export const pwaNavActivePillClass = 'rounded-full px-2 py-1';

/** Hover для glass-чипов (сегменты, фильтр точки). */
export const pwaGlassChipHoverClass = 'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]';

/** Активный сегмент строки вкладок раздела (чаты, смены, уведомления…). */
export const pwaNavSegmentActiveClass = cn(
  pwaRadius.pill,
  'inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition',
  pwaNavActiveClass,
  'rounded-full bg-[var(--nav-active-pill)]'
);

/** Скользящая капсула шапок разделов (мягкий --nav-active, как иконка Смена/Команда/Чат). */
export const pwaShiftSlidingPillClass =
  'pointer-events-none absolute rounded-full bg-[var(--nav-active)] shadow-[0_8px_20px_color-mix(in_srgb,var(--nav-active)_38%,transparent)] transition-[left,width,top] duration-300 ease-out';

export const pwaShiftSlidingSegmentActiveClass =
  'relative z-10 text-white';

export const pwaShiftSlidingSegmentInactiveClass = cn(
  'relative z-10',
  pwaNavInactiveClass
);

/** Неактивный сегмент строки вкладок раздела. */
export const pwaNavSegmentInactiveClass = cn(
  pwaRadius.pill,
  'inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition',
  pwaNavInactiveClass,
  pwaGlassChipHoverClass
);

/** Кнопка фильтра точки без активного выбора — тот же glass. */
export const pwaGlassFilterChipClass = cn(
  pwaGlassSegmentTrackClass,
  'rounded-xl text-[var(--muted-foreground)] transition',
  pwaGlassChipHoverClass
);

/** Капсула фильтра точки в шапке раздела (Telegram-like pill, фикс. высота). */
export const pwaLocationFilterCapsuleClass = cn(
  'pwa-glass-pill pwa-glass-elevated inline-flex h-9 min-h-9 w-fit max-w-[min(38vw,11rem)] shrink-0 items-center gap-1 rounded-full border border-[var(--pwa-glass-border)] px-2 text-xs font-medium text-[var(--foreground)] transition sm:gap-1.5 sm:px-2.5',
  pwaGlassChipHoverClass
);

/** Заголовок раздела по центру второй строки шапки. */
export const pwaSectionHeaderTitleClass =
  'pointer-events-none absolute left-1/2 top-1/2 max-w-[min(100%,10rem)] -translate-x-1/2 -translate-y-1/2 truncate text-center text-[17px] font-semibold leading-tight text-[var(--foreground)]';

export const pwaSegmentPillBaseClass = cn(
  pwaRadius.pill,
  'inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition'
);

export const pwaSegmentPillActiveClass = cn(
  pwaSegmentPillBaseClass,
  'border border-[var(--pwa-glass-border)] bg-[var(--surface-strong)] text-[var(--foreground)] shadow-sm',
  'dark:pwa-glass-pill dark:bg-transparent'
);

export const pwaSegmentPillInactiveClass = cn(
  pwaSegmentPillBaseClass,
  'text-[var(--muted-foreground)]',
  pwaGlassChipHoverClass
);

/** Дорожка сегмент-контроля (компактная, как инциденты/задачи). */
export const pwaSegmentedControlTrackClass = 'p-1';

/** Иконки действий во 2-й строке шапки и в header-pill. */
export const pwaHeaderIconClass = 'h-[18px] w-[18px] shrink-0 text-[var(--foreground)]';

/** Сегмент иконки внутри header-pill (круглая зона нажатия). */
export const pwaHeaderIconCapsuleSegmentClass = cn(
  'native-tap flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition active:scale-[0.98]',
  'text-[var(--foreground)] hover:text-[var(--foreground)]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/45 focus-visible:ring-offset-0'
);

const pwaHeaderIconCapsuleFillBaseClass =
  'shadow-[0_3px_10px_rgba(15,23,42,0.12)] dark:shadow-[0_3px_12px_rgba(0,0,0,0.35)]';

/** Заливки иконок в отдельных капсулах (+, поиск). */
export const pwaHeaderIconCapsuleFillClass = {
  neutral: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'pwa-glass-pill pwa-glass-elevated border border-[var(--pwa-glass-border)] bg-transparent',
    pwaGlassChipHoverClass
  ),
  /** @deprecated Prefer createGreenPale for header create actions. */
  createGreen: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-emerald-700/80 bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 dark:border-emerald-600/90'
  ),
  /** @deprecated Prefer createSupplyPale. */
  createLightGreen: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-green-600/70 bg-green-500 text-white hover:bg-green-600 active:bg-green-700 dark:border-green-500/80 dark:bg-green-600'
  ),
  /** @deprecated Prefer createRedPale. */
  createRed: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-red-700/80 bg-red-600 text-white hover:bg-red-700 active:bg-red-800 dark:border-red-600/90'
  ),
  createRedPale: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-red-500/25 bg-red-500/10 text-red-600 hover:bg-red-500/15 active:bg-red-500/20 dark:border-red-400/30 dark:bg-red-500/15 dark:text-red-300'
  ),
  createGreenPale: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-emerald-600/25 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15 active:bg-emerald-500/20 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-300'
  ),
  createSupplyPale: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-green-600/25 bg-green-500/10 text-green-700 hover:bg-green-500/15 active:bg-green-500/20 dark:border-green-400/30 dark:bg-green-500/15 dark:text-green-300'
  ),
  createShiftPale: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-sky-600/25 bg-sky-500/10 text-sky-700 hover:bg-sky-500/15 active:bg-sky-500/20 dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-300'
  ),
  createAmberPale: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-amber-600/25 bg-amber-500/10 text-amber-800 hover:bg-amber-500/15 active:bg-amber-500/20 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-200'
  ),
  createRosePale: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-rose-600/25 bg-rose-500/10 text-rose-700 hover:bg-rose-500/15 active:bg-rose-500/20 dark:border-rose-400/30 dark:bg-rose-500/15 dark:text-rose-300'
  ),
  createPrimaryPale: cn(
    pwaHeaderIconCapsuleFillBaseClass,
    'border border-primary-600/25 bg-primary-500/10 text-primary-700 hover:bg-primary-500/15 active:bg-primary-500/20 dark:border-primary-400/30 dark:bg-primary-500/15 dark:text-primary-300'
  ),
} as const;

/** Общая капсула pill во 2-й строке (делители между сегментами). */
export const pwaHeaderActionsPillClass = cn(
  pwaLocationFilterCapsuleClass,
  'max-w-none gap-0 px-1 py-0 shadow-[0_3px_10px_rgba(15,23,42,0.1)] dark:shadow-[0_3px_12px_rgba(0,0,0,0.32)]'
);

export const pwaHeaderActionsPillDividerClass =
  'mx-0.5 h-5 w-px shrink-0 bg-[var(--border)]';

/** Колокольчик в верхнем AppBar — glass pill как у фильтра точки. */
export const pwaAppBarBellCapsuleClass = cn(
  pwaLocationFilterCapsuleClass,
  'relative h-9 w-9 min-w-9 max-w-none justify-center gap-0 px-0 shadow-[0_3px_10px_rgba(15,23,42,0.1)] dark:shadow-[0_3px_12px_rgba(0,0,0,0.32)]'
);

/** Иконка колокольчика в шапке PWA. */
export const pwaHeaderBellIconClass = 'h-5 w-5';

/** Красный счётчик на колокольчике. */
export const pwaHeaderBellBadgeClass =
  'absolute -right-0.5 -top-2 flex min-h-[14px] min-w-[14px] items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold leading-none text-white';

/** Класс кнопок сегмент-контроля на операционных экранах (смены, уведомления, команда, чат). */
export const pwaOperationalSegmentClass = '!py-1 !gap-0 text-[10px] sm:text-xs';

/** Вкладки списка чатов: одна строка, бейдж в углу подписи. */
export const pwaChatNavSegmentClass =
  '!flex !items-center !justify-center !gap-0 !py-1 !px-0 min-w-0 text-[10px] leading-tight';

/** Красный кружок непрочитанных справа от подписи вкладки чата (без обводки). */
export const pwaChatNavUnreadBadgeClass =
  'flex shrink-0 min-h-[14px] min-w-[14px] -mt-0.5 items-center justify-center rounded-full bg-red-600 px-0.5 text-[9px] font-bold tabular-nums leading-none text-white';

/** Кнопка-лупа «открыть поиск» — только иконка, без серой/белой подложки. */
export const pwaSearchToggleButtonClass = cn(
  'native-tap flex h-10 w-10 shrink-0 items-center justify-center transition active:scale-[0.98]',
  'text-[var(--muted-foreground)] hover:text-[var(--foreground)]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]'
);

/** Иконка лупы в кнопке поиска. */
export const pwaSearchToggleIconClass = 'h-[18px] w-[18px]';

export const pwaSearchToggleButtonOpenClass = 'text-primary-600 dark:text-primary-400';

/** Поле поиска (Dodo-like filled): stadium, soft surface, без тяжёлой рамки. */
export const pwaSearchFieldClass = cn(
  'h-11 min-w-0 w-full rounded-full border-0 bg-[var(--surface-strong)] px-4 text-sm text-[var(--foreground)]',
  'placeholder:text-[var(--muted-foreground)]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/45 focus-visible:ring-offset-0'
);

/** Stroke line-иконок нижнего таббара / lobby. */
export const pwaMobileTabIconStroke = 1.85;

/** Крупная сегмент-навигация (зарплата, смены, инструкции): на всю ширину, без переноса. */
export const pwaPrimaryNavSegmentClass = 'py-3 text-xs sm:text-sm whitespace-nowrap';

/** Бейдж счётчика на активном сегменте (чат, уведомления). */
export const pwaSegmentBadgeActiveClass =
  'bg-primary-600 text-white dark:bg-primary-500';

/** Бейдж счётчика на неактивном сегменте. */
export const pwaSegmentBadgeInactiveClass =
  'bg-[var(--border-strong)] text-[var(--muted-foreground)]';

/** Пузырёк своего сообщения в чате */
export const pwaChatOwnBubbleClass = cn(pwaPrimaryDiscClass, 'border border-primary-500');

/** Фокус для круглых/квадратных иконок в композере чата и FAB (клавиатурная навигация). */
export const pwaChatIconFocusClass = cn(
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
  'ring-offset-[var(--surface)]'
);

/** Фокус для иконок на glass-фоне чата (без смещения кольца на серый). */
export const pwaChatGlassIconFocusClass = cn(
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-0'
);

/** Фокус для белых чипов композера и шапки треда — без зелёного кольца. */
export const pwaChatControlFocusClass = cn(
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400/60 dark:focus-visible:ring-neutral-500 focus-visible:ring-offset-0'
);

/** Верхняя полоса внутри треда — без матового слоя, иконки на обоях. */
export const pwaChatGlassBarClass =
  'border-transparent bg-transparent';

/** Оболочка композера — лёгкая подложка над обоями (прозрачность в градиенте при клавиатуре). */
export const pwaChatGlassComposerShellClass =
  'chat-composer-shell border-transparent bg-transparent';

/** Капсула поля ввода (textarea + эмодзи) — stadium filled в семье search/fields. */
export const pwaChatGlassInputCapsuleClass = cn(
  'chat-tg-glass-composer chat-tg-glass-pill min-h-11 rounded-full',
  'bg-[var(--surface-strong)] dark:bg-[var(--surface-strong)]'
);

/** Круглая кнопка (шапка и пр.) — frosted glass на обоях. */
export const pwaChatGlassCircleButtonClass = cn(
  'chat-tg-glass chat-tg-glass-circle flex flex-shrink-0 items-center justify-center'
);

/** Круглая кнопка композера — плотное стекло без backdrop-filter. */
export const pwaChatComposerGlassCircleClass = cn(
  'chat-tg-glass-composer chat-tg-glass-circle flex flex-shrink-0 items-center justify-center'
);

/** Круглая кнопка в шапке треда (назад, поиск) — 40px glass. */
export const pwaChatHeaderRoundButtonClass = cn(
  pwaChatGlassCircleButtonClass,
  'h-10 w-10'
);

/** Центральная капсула шапки треда (аватар + имя + статус). */
export const pwaChatHeaderCenterPillClass = cn(
  'chat-tg-glass-pill-header inline-flex min-h-12 max-w-full min-w-0 items-center justify-center gap-1.5 px-2.5 py-1'
);

/** Типографика имени в капсуле шапки. */
export const pwaChatHeaderTitleClass =
  'line-clamp-2 w-full break-words text-center text-[17px] font-semibold leading-tight text-[var(--chat-tg-text-primary)]';

/** Типографика подписи в капсуле шапки. */
export const pwaChatHeaderSubtitleClass =
  'min-h-4 w-full truncate text-center text-[13px] leading-tight text-[var(--chat-tg-text-subtle)]';

/** Ряд композера: скрепка | поле | микрофон — кнопки по нижнему краю при росте текста. */
export const pwaChatComposerRowClass = 'flex min-h-11 items-center gap-2';

/** Кнопка композера 44×44 (скрепка, микрофон, send). */
export const pwaChatComposerCircleButtonClass = cn(pwaChatComposerGlassCircleClass, 'h-11 w-11');

/** FAB «вниз» на обоях чата — offset под цвет фона ленты */
export const pwaChatFabFocusClass = cn(
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
  'ring-offset-[var(--background)]'
);

/** Pill-вкладки (RoomList, HrChatStyleTabRow): активное состояние (без ring — только фон и тень) */
export const pwaChatStyleTabActiveClass =
  'bg-[var(--primary-soft)] text-primary-800 shadow-sm dark:text-primary-100';

/** Неактивная подпись — как у PwaSegmentedControl, не жёсткий --foreground */
export const pwaChatStyleTabInactiveClass =
  'text-[var(--muted-foreground)] hover:bg-[var(--surface)]/80';

/** Бейдж счётчика на неактивной вкладке */
export const pwaChatStyleTabBadgeInactiveClass =
  'bg-[var(--border-strong)] text-[var(--muted-foreground)]';

/** HR-вкладки (HrChatStyleTabRow): сплошная заливка активной пилюли, без полупрозрачных hover */
export const pwaHrTabActiveClass =
  'bg-primary-600 text-white shadow-sm dark:bg-primary-500 dark:text-white';

export const pwaHrTabInactiveClass =
  'text-[var(--muted-foreground)] hover:bg-[var(--surface-strong)]';

/** Счётчик на активной HR-пилюле — контраст к primary-фону */
export const pwaHrTabBadgeActiveClass =
  'bg-white text-primary-700 dark:bg-white/95 dark:text-primary-800';
